import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message as toast } from "antd";

import { ExerciseRecordingStatus, type ExerciseRecording } from "@fitness-app/data-models/entities/ExerciseRecording";

import { analyticsService, supabase } from "~/store/initializeStore";
import { EXERCISE_RECORDINGS_KEY } from "./useTraineeExerciseRecordings";

export const useUpdateExerciseRecording = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    retry: 5,
    async mutationFn(data: Partial<ExerciseRecording> & { id: string }) {
      const { id, ...rest } = data;
      return supabase
        .from("exercise_recording")
        .update({ ...rest, updatedAt: new Date().toISOString() })
        .eq("id", id);
    },
    async onSuccess({ error }) {
      if (error) {
        void toast.error(error.message);
      } else {
        await Promise.all([
          queryClient.invalidateQueries(["exercise_recordings"]),
          // Recordings in the client cards (`[...traineeKeys.all(id), "exerciseRecordings"]`), for any client.
          queryClient.invalidateQueries({
            predicate: (query) => query.queryKey[0] === "trainee" && query.queryKey[2] === EXERCISE_RECORDINGS_KEY,
          }),
        ]);
      }
    },
  });

  const addExerciseRecordingFeedback = async (recordingId: string, feedbackId: string) => {
    analyticsService.track("exercise_recording_feedback_added");
    return mutation.mutateAsync({
      status: ExerciseRecordingStatus.RATED,
      id: recordingId,
      feedbackId,
      updatedAt: new Date().toISOString(),
    });
  };

  const setExerciseRecordingVideo = async (url: string, path: string, recording: ExerciseRecording) => {
    analyticsService.track("exercise_recording_video_uploaded_by_trainer");
    return mutation.mutateAsync({
      url,
      status: ExerciseRecordingStatus.RECORDED,
      id: recording.id,
      metadata: {
        ...(recording.metadata || {}),
        path,
        addedAt: new Date().toISOString(),
        uploadedByTrainer: true,
      },
    });
  };

  /**
   * Back to a blank scheduled request, so the client can record that day again.
   *
   * What the upload left behind has to go with it, or the next recording misbehaves: `feedbackId`
   * would point at a rating for a video that no longer exists, `trainerNotifiedAt` would silence
   * the notification for the re-upload (`HandleExerciseRecordingUpdateUseCase` reads it as
   * "trainers already told"), and the two reassign markers would make the row ineligible for
   * `reassignSentRecordingToNearestScheduledRequest`.
   *
   * The transcode artefacts (`processedPath`, `thumbnailPath`, …) are deliberately left in place:
   * clearing `url` is what queues `process-exercise-recording-video-task` in `cleanup` mode, and
   * that task needs those paths to find the derived files — it nulls them itself once they are
   * deleted. Wiping them here would orphan the files in storage.
   *
   * The score engine reads the status, so dropping back to `scheduled` is also what takes a
   * challenge's points away.
   */
  const deleteExerciseRecordingVideo = async (recording: ExerciseRecording) => {
    analyticsService.track("exercise_recording_video_deleted_by_trainer");
    return mutation.mutateAsync({
      url: null,
      status: ExerciseRecordingStatus.SCHEDULED,
      id: recording.id,
      feedbackId: null,
      metadata: {
        ...(recording.metadata || {}),
        path: null,
        addedAt: null,
        trainerNotifiedAt: null,
        uploadedByTrainer: null,
        reassignedFromRecordingId: null,
        reassignedAt: null,
      },
    });
  };

  const archiveExerciseRecording = async (recording: ExerciseRecording) => {
    analyticsService.track("exercise_recording_archived_by_trainer");
    return mutation.mutateAsync({
      status: ExerciseRecordingStatus.ARCHIVED,
      id: recording.id,
      metadata: {
        ...(recording.metadata || {}),
        archivedAt: new Date().toISOString(),
      },
    });
  };

  return {
    addExerciseRecordingFeedback,
    setExerciseRecordingVideo,
    deleteExerciseRecordingVideo,
    archiveExerciseRecording,
    isUpdating: mutation.isLoading,
  };
};
